import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, Ban, ShieldCheck, TrendingUp } from 'lucide-react';
import { useState } from 'react';
import { formatMoney } from '@nokowazzi/types';
import type { CurrencyCode } from '@nokowazzi/types';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  EmptyState,
  Input,
  Spinner,
  Textarea,
  TrustBadge,
  toast,
} from '@nokowazzi/ui';
import { Table, Td, Th, Thead, Tr } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { Can } from '../features/admin/Can';
import { OrderStatusBadge } from '../features/orders/status';
import { adminUserDetailQuery } from '../features/users/queries';
import { ApiError, apiFetch } from '../lib/api';
import { formatDateTime } from '../lib/datetime';
import { imagePublicUrl } from '../lib/storage';

export const Route = createFileRoute('/_admin/users/$id')({
  component: UserDetailScreen,
});

const COUNTRY_LABEL: Record<string, string> = { KR: '한국', JP: '일본' };

const TRUST_EVENT_LABEL: Record<string, string> = {
  settled_sale: '거래 정산',
  settled_purchase: '구매 정산',
  review_received: '리뷰 수신',
  dispute_lost: '분쟁 패소',
  admin_adjust: '관리자 조정',
};

function UserDetailScreen() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const detailQuery = useQuery(adminUserDetailQuery(id));

  const [suspendOpen, setSuspendOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [suspendError, setSuspendError] = useState<string | null>(null);

  const [trustOpen, setTrustOpen] = useState(false);
  const [trustDelta, setTrustDelta] = useState('');
  const [trustReason, setTrustReason] = useState('');
  const [trustErrors, setTrustErrors] = useState<{ delta?: string; reason?: string }>({});

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  const suspendMutation = useMutation({
    mutationFn: (input: { suspended: boolean; reason: string }) =>
      apiFetch<unknown>(`/v1/admin/users/${id}/suspend`, { method: 'POST', json: input }),
    onSuccess: async () => {
      await invalidate();
      toast.show({ type: 'success', message: '처리되었습니다' });
      setSuspendOpen(false);
      setSuspendReason('');
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '처리에 실패했습니다',
      });
    },
  });

  const trustMutation = useMutation({
    mutationFn: (input: { delta: number; reason: string }) =>
      apiFetch<unknown>(`/v1/admin/users/${id}/trust-adjust`, { method: 'POST', json: input }),
    onSuccess: async () => {
      await invalidate();
      toast.show({ type: 'success', message: '신뢰도를 조정했습니다' });
      setTrustOpen(false);
      setTrustDelta('');
      setTrustReason('');
    },
    onError: (error) => {
      toast.show({
        type: 'error',
        message: error instanceof ApiError ? error.message : '조정에 실패했습니다',
      });
    },
  });

  if (detailQuery.isPending) {
    return (
      <div className="flex min-h-52 items-center justify-center">
        <Spinner label="회원 불러오는 중" />
      </div>
    );
  }

  if (detailQuery.isError) {
    return (
      <EmptyState
        title="회원을 불러오지 못했습니다"
        description={
          detailQuery.error instanceof ApiError ? detailQuery.error.message : '잠시 후 다시 시도해 주세요'
        }
        action={
          <Button variant="tertiary" size="sm" onClick={() => void detailQuery.refetch()}>
            다시 시도
          </Button>
        }
      />
    );
  }

  const { profile, private: priv, recentOrders, trustEvents } = detailQuery.data;
  const isMasked = priv === undefined;

  const submitSuspend = () => {
    if (suspendReason.trim() === '') {
      setSuspendError('사유를 입력해 주세요');
      return;
    }
    setSuspendError(null);
    suspendMutation.mutate({ suspended: !profile.is_suspended, reason: suspendReason.trim() });
  };

  const submitTrust = () => {
    const nextErrors: { delta?: string; reason?: string } = {};
    const delta = Number(trustDelta);
    if (trustDelta.trim() === '' || !Number.isFinite(delta) || delta === 0) {
      nextErrors.delta = '0이 아닌 조정값을 입력해 주세요';
    }
    if (trustReason.trim() === '') nextErrors.reason = '사유를 입력해 주세요';
    if (Object.keys(nextErrors).length > 0) {
      setTrustErrors(nextErrors);
      return;
    }
    setTrustErrors({});
    trustMutation.mutate({ delta, reason: trustReason.trim() });
  };

  return (
    <>
      <div className="mb-4">
        <Link
          to="/users"
          className="inline-flex items-center gap-1.5 text-caption1 text-fg-secondary hover:text-fg"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          회원 목록
        </Link>
      </div>

      <PageHeader
        title={profile.display_name}
        description={`@${profile.handle}`}
        actions={
          <div className="flex items-center gap-2">
            <Can perm="users:trust_adjust">
              <Button
                variant="tertiary"
                size="sm"
                leftIcon={<TrendingUp className="size-4" aria-hidden="true" />}
                onClick={() => {
                  setTrustDelta('');
                  setTrustReason('');
                  setTrustErrors({});
                  setTrustOpen(true);
                }}
              >
                신뢰도 조정
              </Button>
            </Can>
            <Can perm="users:sanction">
              <Button
                variant={profile.is_suspended ? 'secondary' : 'danger'}
                size="sm"
                leftIcon={
                  profile.is_suspended ? (
                    <ShieldCheck className="size-4" aria-hidden="true" />
                  ) : (
                    <Ban className="size-4" aria-hidden="true" />
                  )
                }
                onClick={() => {
                  setSuspendReason('');
                  setSuspendError(null);
                  setSuspendOpen(true);
                }}
              >
                {profile.is_suspended ? '정지 해제' : '계정 정지'}
              </Button>
            </Can>
          </div>
        }
      />

      <div className="grid grid-cols-3 gap-6">
        {/* 프로필 */}
        <section className="rounded-lg border border-line bg-surface p-5">
          <div className="mb-4 flex items-center gap-3">
            <Avatar
              size="lg"
              name={profile.display_name}
              src={profile.avatar_path != null ? imagePublicUrl(profile.avatar_path) : undefined}
            />
            <div className="min-w-0">
              <p className="truncate text-title3 text-fg">{profile.display_name}</p>
              <p className="truncate text-caption1 text-fg-tertiary">@{profile.handle}</p>
            </div>
          </div>
          <dl className="flex flex-col gap-2 text-caption1">
            <Row label="국가">{COUNTRY_LABEL[profile.country] ?? profile.country}</Row>
            <Row label="언어">{profile.locale}</Row>
            <Row label="이메일">
              {isMasked ? (
                <span className="inline-flex items-center gap-1 text-fg-tertiary">
                  <Badge tone="neutral">마스킹</Badge>
                </span>
              ) : (
                <span className="text-fg">{priv.email ?? '—'}</span>
              )}
            </Row>
            <Row label="상태">
              {profile.is_suspended ? <Badge tone="danger">정지</Badge> : <Badge tone="success">정상</Badge>}
            </Row>
            <Row label="가입일">{formatDateTime(profile.created_at)}</Row>
          </dl>
          {profile.bio != null && profile.bio !== '' && (
            <p className="mt-3 border-t border-line pt-3 text-caption1 text-fg-secondary">{profile.bio}</p>
          )}
        </section>

        {/* 신뢰도 */}
        <section className="rounded-lg border border-line bg-surface p-5">
          <h2 className="mb-3 text-title3 text-fg">신뢰도</h2>
          <TrustBadge
            altitude={profile.trust_score}
            size="lg"
            meterAriaLabel={`신뢰도 ${profile.trust_score}미터`}
          />
          <p className="mt-2 text-caption2 text-fg-tertiary">레벨 {profile.trust_level}</p>

          <h3 className="mb-2 mt-5 text-caption1 font-medium text-fg-secondary">최근 이력</h3>
          {trustEvents.length === 0 ? (
            <p className="text-caption1 text-fg-tertiary">이력이 없습니다</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {trustEvents.map((event) => (
                <li key={event.id} className="flex items-center justify-between gap-2 text-caption1">
                  <span className="min-w-0 truncate text-fg-secondary">
                    {TRUST_EVENT_LABEL[event.event_type] ?? event.event_type}
                  </span>
                  <span
                    className={
                      event.points >= 0
                        ? 'shrink-0 tabular-nums text-success-700'
                        : 'shrink-0 tabular-nums text-danger-600'
                    }
                  >
                    {event.points >= 0 ? '+' : ''}
                    {event.points}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 최근 거래 */}
        <section className="rounded-lg border border-line bg-surface p-5">
          <h2 className="mb-3 text-title3 text-fg">최근 거래</h2>
          {recentOrders.length === 0 ? (
            <p className="text-caption1 text-fg-tertiary">거래 내역이 없습니다</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {recentOrders.map((order) => (
                <li key={order.id}>
                  <Link
                    to="/orders/$id"
                    params={{ id: order.id }}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Badge tone="neutral">{order.role === 'buyer' ? '구매' : '판매'}</Badge>
                      <OrderStatusBadge status={order.status} />
                    </span>
                    <span className="shrink-0 tabular-nums text-caption1 text-fg">
                      {formatMoney({ amount: order.total_amount, currency: order.currency as CurrencyCode })}
                    </span>
                  </Link>
                  <p className="mt-0.5 text-caption2 text-fg-tertiary">{formatDateTime(order.created_at)}</p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* 최근 거래 상세 테이블 (넓게) */}
      {recentOrders.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-3 text-title3 text-fg">거래 상세</h2>
          <Table>
            <Thead>
              <tr>
                <Th className="w-28">주문번호</Th>
                <Th className="w-20">역할</Th>
                <Th className="w-28">상태</Th>
                <Th className="text-right">금액</Th>
                <Th className="w-36">생성일</Th>
              </tr>
            </Thead>
            <tbody>
              {recentOrders.map((order) => (
                <Tr key={order.id} className="hover:bg-subtle">
                  <Td>
                    <Link
                      to="/orders/$id"
                      params={{ id: order.id }}
                      className="font-mono text-caption1 font-medium text-fg-brand hover:underline"
                    >
                      {order.id.slice(0, 8)}
                    </Link>
                  </Td>
                  <Td className="text-fg-secondary">{order.role === 'buyer' ? '구매자' : '판매자'}</Td>
                  <Td>
                    <OrderStatusBadge status={order.status} />
                  </Td>
                  <Td className="text-right tabular-nums">
                    {formatMoney({ amount: order.total_amount, currency: order.currency as CurrencyCode })}
                  </Td>
                  <Td className="whitespace-nowrap text-caption2 text-fg-tertiary">
                    {formatDateTime(order.created_at)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </section>
      )}

      {/* 정지/해제 Dialog */}
      <Dialog
        open={suspendOpen}
        onOpenChange={(open) => {
          if (!open) setSuspendOpen(false);
        }}
        title={profile.is_suspended ? '계정 정지 해제' : '계정 정지'}
        description={
          profile.is_suspended
            ? '이 회원의 정지를 해제합니다. 사유를 입력하세요.'
            : '이 회원의 계정을 정지합니다. 정지된 회원은 로그인 및 거래가 제한됩니다.'
        }
        footer={
          <>
            <Button variant="tertiary" fullWidth onClick={() => setSuspendOpen(false)}>
              취소
            </Button>
            <Button
              variant={profile.is_suspended ? 'secondary' : 'danger'}
              fullWidth
              loading={suspendMutation.isPending}
              onClick={submitSuspend}
            >
              {profile.is_suspended ? '정지 해제' : '정지'}
            </Button>
          </>
        }
      >
        <Textarea
          label="사유 (필수)"
          value={suspendReason}
          onChange={(e) => {
            setSuspendReason(e.target.value);
            if (suspendError != null) setSuspendError(null);
          }}
          maxLength={500}
          rows={3}
          error={suspendError ?? undefined}
        />
      </Dialog>

      {/* 신뢰도 조정 Dialog */}
      <Dialog
        open={trustOpen}
        onOpenChange={(open) => {
          if (!open) setTrustOpen(false);
        }}
        title="신뢰도 조정"
        description="와찌 고도(m)를 수동 조정합니다. 양수/음수 모두 가능합니다."
        footer={
          <>
            <Button variant="tertiary" fullWidth onClick={() => setTrustOpen(false)}>
              취소
            </Button>
            <Button variant="secondary" fullWidth loading={trustMutation.isPending} onClick={submitTrust}>
              조정 실행
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Input
            label="조정값 (m)"
            type="number"
            inputMode="numeric"
            placeholder="예: +5 또는 -3"
            value={trustDelta}
            onChange={(e) => setTrustDelta(e.target.value)}
            error={trustErrors.delta}
          />
          <Textarea
            label="사유 (필수)"
            value={trustReason}
            onChange={(e) => setTrustReason(e.target.value)}
            maxLength={500}
            rows={3}
            error={trustErrors.reason}
          />
        </div>
      </Dialog>
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-fg-tertiary">{label}</dt>
      <dd className="text-right text-fg">{children}</dd>
    </div>
  );
}
